import * as authStore from '../stores/authStore';
import * as userStore from '../stores/userStore';
import { consumeBatchContinuousWait } from './batchContinuousWait';
import { trackMetricsEvent } from './trackMetricsEvent';

export type TrackOutputPreviewedOptions = {
  eventSource: string;
  app?: string;
  jobID?: string;
  batchID?: string;
  /**
   * How the user first saw motion, e.g. `studioPreview` (tile MP4),
   * `bvh` (3D preview modal), or `outputVideo` (mascot MP4).
   */
  format?: string;
  /**
   * When true (default) and `batchID` is set, emit at most once per batch for
   * this page session — used for time-to-first-frame.
   */
  oncePerBatch?: boolean;
};

/** Batches that already emitted `outputPreviewed` this page session. */
const previewedBatchIDs = new Set<string>();

/**
 * Fire-and-forget UI emit for Metrics `outputPreviewed`.
 * Failures are ignored so preview UX is never blocked by analytics.
 *
 * Always attaches the current metrics working-session ID. When `batchID` is
 * set, also sets `continuousWait` so TTFF queries can exclude return visits.
 */
export function trackOutputPreviewed({
  eventSource,
  app,
  jobID,
  batchID,
  format,
  oncePerBatch = true,
}: TrackOutputPreviewedOptions): void {
  const batchKey = batchID?.trim();
  if (oncePerBatch && batchKey) {
    if (previewedBatchIDs.has(batchKey)) return;
    previewedBatchIDs.add(batchKey);
  }

  const continuousWait = batchKey
    ? consumeBatchContinuousWait(batchKey)
    : false;

  const properties: Record<string, unknown> = {
    continuousWait,
  };
  if (jobID) properties.jobID = jobID;
  if (batchKey) properties.batchID = batchKey;
  if (format) properties.format = format;

  const userID = userStore.state.userInfo.userID?.trim() || undefined;
  const sessionID = authStore.getWorkingSessionID();

  void trackMetricsEvent({
    eventName: 'outputPreviewed',
    eventSource,
    sessionID,
    ...(app ? { app } : {}),
    ...(userID ? { createdByUserID: userID } : {}),
    properties,
  });
}
